import { useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { useGiveaway } from './GiveawayProvider';

function decodeText(value) {
    if (!value) return '';

    if (typeof document === 'undefined') {
        return String(value)
            .replace(/&#8211;|&ndash;/g, '–')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#039;/g, "'");
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value);
    return textarea.value;
}

function getPackTokens(activeToken) {
    return (Array.isArray(activeToken?.tokens) ? activeToken.tokens : [activeToken]).filter(Boolean);
}

function isRealUrl(url) {
    return typeof url === 'string' && /^https?:\/\//i.test(url);
}

function RewardList({ previews }) {
    if (!previews.length) {
        return (
            <p className="bjnt-giveaway-pack-card__empty">
                Collect this sticker to add it to your giveaway profile.
            </p>
        );
    }

    return (
        <ul className="bjnt-giveaway-pack-card__entries">
            {previews.map((entry, entryIndex) => {
                const count = Number(entry.count || 1);
                const label = decodeText(entry.label || 'Giveaway prize');
                const entryWord = count === 1 ? 'entry' : 'entries';
                const url = isRealUrl(entry.url) ? entry.url : '';

                return (
                    <li key={`${entry.type || entryIndex}-${label}`}>
                        <span className="bjnt-giveaway-pack-card__entry-count">+{count} {entryWord}</span>

                        {url ? (
                            <a href={url} target="_blank" rel="noreferrer">
                                {label}
                            </a>
                        ) : (
                            <span className="bjnt-giveaway-pack-card__entry-prize">{label}</span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

function StickerPackCarousel({ tokens }) {
    const [activeIndex, setActiveIndex] = useState(0);

    if (!tokens.length) return null;

    const safeIndex = Math.min(activeIndex, tokens.length - 1);
    const token = tokens[safeIndex] || tokens[0];
    const previews = Array.isArray(token.entryPreview) ? token.entryPreview : [];
    const partnerName = decodeText(token.partnerName || token.name || 'giveaway');
    const tokenName = decodeText(token.name || 'Sticker');
    const hasMultiple = tokens.length > 1;

    const goTo = (nextIndex) => {
        const bounded = Math.max(0, Math.min(tokens.length - 1, nextIndex));
        setActiveIndex(bounded);
    };

    return (
        <div className="bjnt-giveaway-pack-carousel" aria-label="Sticker pack rewards">
            <article className="bjnt-giveaway-pack-card">
                <div className="bjnt-giveaway-pack-card__visual">
                    <div className="bjnt-giveaway-pack-card__halo" />
                    <div className="bjnt-giveaway-pack-card__sticker">
                        {token.imageUrl ? (
                            <img src={token.imageUrl} alt="" />
                        ) : (
                            <span>{tokenName}</span>
                        )}
                    </div>
                </div>

                <div className="bjnt-giveaway-pack-card__body">
                    {tokens.length > 1 &&
                        <p className="bjnt-giveaway-pack-card__eyebrow">
                            Sticker {safeIndex + 1} of {tokens.length}
                        </p>
                    }

                    <h3>{partnerName} sticker</h3>

                    <RewardList previews={previews} />
                </div>
            </article>

            {hasMultiple ? (
                <div className="bjnt-giveaway-pack-pills" aria-label="Choose sticker">
                    <button
                        type="button"
                        className="bjnt-giveaway-pack-arrow"
                        onClick={() => goTo(safeIndex - 1)}
                        disabled={safeIndex === 0}
                        aria-label="Previous sticker"
                    >
                        ‹
                    </button>

                    <div className="bjnt-giveaway-pack-pill-track">
                        {tokens.map((item, index) => {
                            const isActive = index === safeIndex;
                            const itemName = decodeText(item.partnerName || item.name || `Sticker ${index + 1}`);

                            return (
                                <button
                                    key={`${item.claimToken || item.tokenId || index}`}
                                    type="button"
                                    className={`bjnt-giveaway-pack-pill${isActive ? ' is-active' : ''}`}
                                    onClick={() => goTo(index)}
                                    aria-label={`View ${itemName} sticker`}
                                    aria-current={isActive ? 'true' : undefined}
                                >
                                    {item.imageUrl ? (
                                        <img src={item.imageUrl} alt="" />
                                    ) : (
                                        <span>{index + 1}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        className="bjnt-giveaway-pack-arrow"
                        onClick={() => goTo(safeIndex + 1)}
                        disabled={safeIndex === tokens.length - 1}
                        aria-label="Next sticker"
                    >
                        ›
                    </button>
                </div>
            ) : null}
        </div>
    );
}

export default function GiveawayLoginModal() {
    const {
        showLogin,
        showCollect,
        activeToken,
        loading,
        message,
        closeModals,
        requestLoginCode,
        verifyLoginCode,
        showEligibility,
        acceptEligibility,
        collectActiveToken,
    } = useGiveaway();

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [code, setCode] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [ageConfirmed, setAgeConfirmed] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const dialogRef = useRef(null);

    const tokens = useMemo(() => getPackTokens(activeToken), [activeToken]);
    const packCount = tokens.length;
    const hasPack = packCount > 0;

    useEffect(() => {
        if (!showLogin && !showCollect && !showEligibility) {
            setCode('');
            setCodeSent(false);
            setAgeConfirmed(false);
            setTermsAccepted(false);
        }
    }, [showLogin, showCollect, showEligibility]);

    useEffect(() => {
        if (!showLogin && !showCollect && !showEligibility) return undefined;

        const dialog = dialogRef.current;
        const ownerDocument = dialog?.ownerDocument || document;
        const previousFocus = ownerDocument.activeElement;
        const focusableSelector = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
        const focusable = dialog ? [...dialog.querySelectorAll(focusableSelector)] : [];

        (focusable[0] || dialog)?.focus();

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeModals();
                return;
            }

            if (event.key !== 'Tab' || !focusable.length) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && ownerDocument.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && ownerDocument.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        ownerDocument.addEventListener('keydown', handleKeyDown);

        return () => {
            ownerDocument.removeEventListener('keydown', handleKeyDown);
            previousFocus?.focus?.();
        };
    }, [showLogin, showCollect, showEligibility, codeSent, closeModals]);

    if (!showLogin && !showCollect && !showEligibility) return null;

    const submitEmail = async (event) => {
        event.preventDefault();

        const sent = await requestLoginCode(email);

        if (sent) {
            setCodeSent(true);
        }
    };

    const submitCode = async (event) => {
        event.preventDefault();
        await verifyLoginCode(email, code);
    };

    return (
        <>
            <button
                type="button"
                className="bjnt-giveaway-overlay"
                onClick={closeModals}
                aria-label="Close giveaway modal"
            />

            <div
                ref={dialogRef}
                className="bjnt-giveaway-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bjnt-giveaway-dialog-title"
                tabIndex="-1"
            >
                <div className="bjnt-giveaway-sheet__header">
                    <div className="bjnt-giveaway-sheet__grab" />

                    <button
                        type="button"
                        className="bjnt-giveaway-sheet__close"
                        onClick={closeModals}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="bjnt-giveaway-sheet__body">
                    <div className="bjnt-giveaway-pack-head">

                        <h2 id="bjnt-giveaway-dialog-title">
                            {hasPack ? (
                                <>
                                    You found{' '}
                                    <span>
                                        {packCount > 1 ? `${packCount} stickers!` : 'a sticker!'}
                                    </span>
                                </>
                            ) : (
                                <>
                                    Track your <span>giveaway progress</span>
                                </>
                            )}
                        </h2>

                        <p>
                            {hasPack
                                ? `Review your sticker reward${packCount > 1 ? 's' : ''}, then collect ${packCount > 1 ? 'the pack' : 'your sticker'}.`
                                : 'Enter your email and we’ll send a quick login code. No password needed.'}
                        </p>
                    </div>

                    {hasPack ? <StickerPackCarousel tokens={tokens} /> : null}

                    {showEligibility ? (
                        <form
                            className="bjnt-giveaway-form"
                            onSubmit={async (event) => {
                                event.preventDefault();

                                await acceptEligibility(
                                    firstName,
                                    lastName,
                                    ageConfirmed,
                                    termsAccepted
                                );
                            }}
                        >
                            <input
                                id="bjnt-giveaway-first-name"
                                type="text"
                                value={firstName}
                                onChange={(event) => setFirstName(event.target.value)}
                                placeholder="First name"
                            />

                            <input
                                id="bjnt-giveaway-last-name"
                                type="text"
                                value={lastName}
                                onChange={(event) => setLastName(event.target.value)}
                                placeholder="Last name"
                            />

                            <label
                                className="bjnt-giveaway-check"
                                htmlFor="bjnt-giveaway-age-confirmed"
                            >
                                <input
                                    id="bjnt-giveaway-age-confirmed"
                                    type="checkbox"
                                    checked={ageConfirmed}
                                    onChange={(event) => setAgeConfirmed(event.target.checked)}
                                    required
                                />
                                <span>
                                    I confirm that I am 18 years of age or older and eligible to participate.
                                </span>
                            </label>

                            <label
                                className="bjnt-giveaway-check"
                                htmlFor="bjnt-giveaway-terms-accepted"
                            >
                                <input
                                    id="bjnt-giveaway-terms-accepted"
                                    type="checkbox"
                                    checked={termsAccepted}
                                    onChange={(event) => setTermsAccepted(event.target.checked)}
                                    required
                                />
                                <span>
                                    I agree to the Giveaway Terms & Conditions and Privacy Policy.
                                </span>
                            </label>

                            <button
                                type="submit"
                                className="bjnt-giveaway-sheet__button"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Continue'}
                            </button>
                        </form>
                    ) : !showCollect ? (
                        <>
                            {!codeSent ? (
                                <form onSubmit={submitEmail} className="bjnt-giveaway-form">
                                    <label>
                                        Email Address
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            required
                                        />
                                    </label>

                                    <button
                                        type="submit"
                                        className="bjnt-giveaway-sheet__button"
                                        disabled={loading}
                                    >
                                        {loading ? 'Sending...' : 'Send Login Code'}
                                    </button>
                                </form>
                            ) : (
                                <form onSubmit={submitCode} className="bjnt-giveaway-form">
                                    <label>
                                        Login Code
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={code}
                                            onChange={(event) => setCode(event.target.value)}
                                            required
                                        />
                                    </label>

                                    <button
                                        type="submit"
                                        className="bjnt-giveaway-sheet__button"
                                        disabled={loading}
                                    >
                                        {loading ? 'Checking...' : 'Verify Code'}
                                    </button>
                                </form>
                            )}
                        </>
                    ) : null}
                </div>

                <div className="bjnt-giveaway-sheet__footer">
                    {showCollect ? (
                        <button
                            type="button"
                            className="bjnt-giveaway-sheet__button"
                            onClick={collectActiveToken}
                            disabled={loading || !activeToken?.claimToken}
                        >
                            {loading ? 'Collecting...' : `Collect ${packCount > 1 ? 'Stickers' : 'Sticker'}`}
                        </button>
                    ) : null}

                    {message ? (
                        <p className="bjnt-giveaway-message" aria-live="polite">
                            {message}
                        </p>
                    ) : null}
                </div>
            </div>
        </>
    );
}
